import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../../components/AppShell";
import StatusBadge from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Compass, Users, IdCard, FileText, ArrowRight, Calendar, MapPin, Bell, Ticket } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [regs, setRegs] = useState([]);
  const [students, setStudents] = useState([]);
  const [membership, setMembership] = useState(null);
  const [forms, setForms] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    api.get("/registrations/me").then((r) => setRegs(r.data)).catch(() => {});
    api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
    api.get("/membership/me").then((r) => setMembership(r.data)).catch(() => {});
    api.get("/form-responses/me").then((r) => setForms(r.data)).catch(() => {});
    api.get("/announcements").then((r) => setAnnouncements(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const upcoming = regs.filter((r) => r.experience && new Date(r.experience.date) >= new Date());
  const next = upcoming[0];
  const firstName = (user?.name || "").split(" ")[0];

  return (
    <AppShell>
      <div className="flex flex-col gap-1">
        <h1 className="font-display text-3xl font-bold text-navy">{greeting()}, {firstName}.</h1>
        <p className="text-slateBrand">Here's what's happening with your family.</p>
      </div>

      {/* NEXT EXPERIENCE */}
      <div className="mt-8">
        {next ? (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white md:flex" data-testid="dashboard-next-experience">
            <div className="h-48 md:h-auto md:w-2/5">
              <img src={next.experience.images?.[0]} alt="" className="h-full w-full object-cover" />
            </div>
            <div className="flex flex-1 flex-col p-6">
              <span className="eyebrow text-brandblue">Your next experience</span>
              <h2 className="mt-2 font-display text-2xl font-bold text-navy">{next.experience.title}</h2>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slateBrand">
                <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" />{new Date(next.experience.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{next.experience.location}</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <StatusBadge status={next.payment_status} testId="dashboard-next-payment" />
                <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slateBrand">{next.students.length} student(s)</span>
              </div>
              <div className="mt-auto pt-5">
                <Button variant="outline" data-testid="dashboard-view-trip-btn" onClick={() => navigate(`/experiences/${next.experience.id}`)} className="font-display font-semibold">
                  View Trip <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <Compass className="mx-auto h-10 w-10 text-brandblue" />
            <h2 className="mt-4 font-display text-xl font-bold text-navy">No upcoming experiences yet</h2>
            <p className="mt-2 text-slateBrand">Find something new to explore.</p>
            <Button data-testid="dashboard-find-experience-btn" onClick={() => navigate("/experiences")} className="mt-5 bg-brandblue font-display font-semibold hover:bg-blue-700">Find an Experience</Button>
          </div>
        )}
      </div>

      {/* QUICK STATS */}
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { icon: Users, label: "Children", value: students.length, to: "/students" },
          { icon: IdCard, label: "Membership", value: membership?.active ? "Explorer" : "None", to: "/membership" },
          { icon: Ticket, label: "Credits", value: membership?.active ? (membership.credits_remaining ?? 0) : "—", to: "/membership" },
          { icon: FileText, label: "Forms Signed", value: forms.length, to: "/forms" },
        ].map((s) => (
          <button key={s.label} data-testid={`dashboard-stat-${s.label.toLowerCase().replace(/ /g, "-")}`} onClick={() => navigate(s.to)}
            className="rounded-xl border border-slate-200 bg-white p-5 text-left card-lift">
            <div className="flex items-center gap-2 text-slateBrand"><s.icon className="h-4 w-4" /><span className="text-sm">{s.label}</span></div>
            <div className="mt-2 font-display text-2xl font-bold text-navy">{s.value}</div>
          </button>
        ))}
      </div>

      {/* REGISTRATIONS + ANNOUNCEMENTS */}
      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h3 className="font-display text-lg font-bold text-navy">Your registrations</h3>
          <div className="mt-4 space-y-3">
            {regs.length === 0 && <p className="text-sm text-slateBrand">No registrations yet.</p>}
            {regs.map((r) => (
              <div key={r.id} data-testid={`dashboard-reg-${r.id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4">
                <div>
                  <div className="font-display font-semibold text-navy">{r.experience?.title}</div>
                  <div className="text-xs text-slateBrand">{r.students.map((s) => s.first_name).join(", ")} • {new Date(r.experience?.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                </div>
                <StatusBadge status={r.status === "waitlisted" ? "waitlisted" : r.payment_status} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-navy"><Bell className="h-4 w-4" />Announcements</h3>
          <div className="mt-4 space-y-3">
            {announcements.map((a) => (
              <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="font-display text-sm font-semibold text-navy">{a.title}</div>
                <p className="mt-1 text-xs text-slateBrand">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
